import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { RefreshCw, Mail, MailX, ShieldCheck, FileWarning, ClipboardList, Download, Sparkles } from "lucide-react";

const KIND_META = {
  cert: { label: "Cert Expiry", tone: "amber", Icon: ShieldCheck },
  incident: { label: "Incident", tone: "red", Icon: FileWarning },
  dar: { label: "DAR", tone: "blue", Icon: ClipboardList },
};

const KIND_TABS = [
  { key: "all", label: "All" },
  { key: "cert", label: "Cert Expiries" },
  { key: "incident", label: "Incidents" },
  { key: "dar", label: "DAR Missing" },
];

/** Admin-only audit log of the last 100 emails sent — cert / incident / DAR. */
export default function NotificationsLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications/log", { params: { kind, limit: 100 } });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load notifications log"));
    } finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const hay = [
        r.subject,
        r.entity_label,
        r.entity_id,
        r.type_detail,
        ...(r.recipients || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, q]);

  const kpi = useMemo(() => {
    const t = { total: rows.length, sent: 0, failed: 0, cert: 0, incident: 0, dar: 0 };
    rows.forEach((r) => {
      if (r.sent) t.sent++; else t.failed++;
      if (r.kind in t) t[r.kind]++;
    });
    return t;
  }, [rows]);

  const sortAcc = useMemo(() => ({
    sent_at: (r) => r.sent_at || "",
    kind: (r) => r.kind || "",
    subject: (r) => (r.subject || "").toLowerCase(),
    recipients: (r) => (r.recipients || []).join(","),
    status: (r) => (r.sent ? "sent" : "failed"),
  }), []);
  const sortState = useSortableRows(filtered, sortAcc, {
    defaultKey: "sent_at",
    defaultDir: "desc",
    storageKey: "notifications-log",
  });

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch { return iso; }
  };

  const exportCsv = () => {
    const q1 = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Sent At", "Kind", "Type", "Subject", "Recipients", "Status", "Email ID", "Reason", "Entity"];
    const lines = [headers.map(q1).join(",")];
    for (const r of sortState.sorted) {
      lines.push([
        r.sent_at || "",
        KIND_META[r.kind]?.label || r.kind,
        r.type_detail || "",
        r.subject || "",
        (r.recipients || []).join("; "),
        r.sent ? "sent" : "failed",
        r.email_id || "",
        r.reason || "",
        r.entity_label || r.entity_id || "",
      ].map(q1).join(","));
    }
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notifications_log_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sortState.sorted.length} rows`);
  };

  const triggerDigest = useCallback(async (cadence) => {
    try {
      const r = await api.post(`/notifications/digest?cadence=${cadence}&force=true`);
      const results = r.data?.results || [];
      const sent = results.reduce((n, x) => n + (x.sent || 0), 0);
      const users = results.reduce((n, x) => n + (x.users || 0), 0);
      toast.success(`${cadence[0].toUpperCase() + cadence.slice(1)} digest fired — ${sent}/${users} sent`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to fire digest"));
    }
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Notifications Log"
        subtitle="Audit trail of every alert email sent by the platform (cert expiries, incident alerts, missing DARs, tardies, geofence breaches, ops digests). Last 100 events."
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1 mr-2" data-testid="digest-preview-group">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1 inline-flex items-center gap-1"><Sparkles className="size-3" /> Preview digest</span>
              {["daily", "weekly", "monthly"].map((c) => (
                <button
                  key={c}
                  data-testid={`digest-preview-${c}`}
                  onClick={() => triggerDigest(c)}
                  className="px-2.5 py-1 text-[10px] uppercase tracking-widest border border-[#27272A] text-zinc-300 hover:text-white hover:border-[#0A84FF]"
                  title={`Send a ${c} digest right now to every user opted-in at that cadence`}
                >{c}</button>
              ))}
            </div>
            <GhostButton data-testid="notifications-log-refresh" onClick={load} title="Reload">
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </GhostButton>
            <GhostButton data-testid="notifications-log-csv" onClick={exportCsv}>
              <Download className="size-3.5" /> Export CSV
            </GhostButton>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <KpiCard testId="log-kpi-total" label="Total in view" value={kpi.total} />
          <KpiCard testId="log-kpi-sent" label="Delivered" value={kpi.sent} tone="green" />
          <KpiCard testId="log-kpi-failed" label="Failed" value={kpi.failed} tone={kpi.failed > 0 ? "red" : "default"} />
          <KpiCard testId="log-kpi-cert" label="Cert" value={kpi.cert} tone="amber" />
          <KpiCard testId="log-kpi-incident" label="Incident" value={kpi.incident} tone="red" />
        </div>

        {/* Filters */}
        <div className="border border-[#27272A] bg-[#121212] p-3 flex flex-wrap items-center gap-2">
          <div className="label-mute mr-1">Kind</div>
          {KIND_TABS.map((t) => {
            const active = kind === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`log-tab-${t.key}`}
                onClick={() => setKind(t.key)}
                className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${active ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5" : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"}`}
              >{t.label}</button>
            );
          })}
          <div className="mx-2 h-6 w-px bg-[#27272A]" />
          <input
            data-testid="log-search"
            type="text"
            placeholder="Search subject, recipient, entity…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={`${inputClass} min-w-[280px] flex-1`}
          />
          <div className="ml-auto text-xs font-mono text-zinc-500">
            {sortState.sorted.length} / {rows.length}
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#27272A] bg-[#121212]" data-testid="notifications-log-table">
          <table className="w-full text-xs">
            <thead className="border-b border-[#27272A]">
              <tr>
                <SortableHeader k="sent_at" state={sortState}>Sent At</SortableHeader>
                <SortableHeader k="kind" state={sortState}>Kind</SortableHeader>
                <SortableHeader k="subject" state={sortState}>Subject</SortableHeader>
                <SortableHeader k="recipients" state={sortState}>Recipients</SortableHeader>
                <SortableHeader k="status" state={sortState}>Status</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">Entity</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={6} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && sortState.sorted.length === 0 && (
                <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">
                  {rows.length === 0
                    ? "No emails have been sent yet. Once Resend is configured and cron scans start firing, entries will appear here."
                    : "No entries match your filters."}
                </td></tr>
              )}
              {sortState.sorted.map((r) => {
                const meta = KIND_META[r.kind] || { label: r.kind, tone: "default", Icon: Mail };
                const Icon = meta.Icon;
                const StatusIcon = r.sent ? Mail : MailX;
                return (
                  <tr key={r.id} data-testid={`log-row-${r.id}`} className="hover:bg-[#1a1a1a]">
                    <td className="px-4 py-3 font-mono text-zinc-300 whitespace-nowrap">{fmtTime(r.sent_at)}</td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-1.5">
                        <Icon className={`size-3.5 ${meta.tone === "red" ? "text-[#FF453A]" : meta.tone === "amber" ? "text-[#FF9F0A]" : meta.tone === "blue" ? "text-[#0A84FF]" : "text-zinc-400"}`} />
                        <PillBadge tone={meta.tone}>{meta.label}</PillBadge>
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-zinc-600 mt-1 font-mono">{r.type_detail}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-200 max-w-md">
                      <div className="truncate" title={r.subject}>{r.subject || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {(r.recipients || []).length > 0 ? (
                        <div className="space-y-0.5" title={(r.recipients || []).join(", ")}>
                          <div className="truncate max-w-[220px]">{r.recipients[0]}</div>
                          {r.recipients.length > 1 && (
                            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">+ {r.recipients.length - 1} more</div>
                          )}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {r.sent ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-[#34C759]/40 text-[#34C759] bg-[#34C759]/10 px-2 py-0.5">
                          <StatusIcon className="size-3" /> Sent
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5"
                          title={r.reason || "Delivery failed"}
                        >
                          <StatusIcon className="size-3" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      <div className="font-medium">{r.entity_label || "—"}</div>
                      {r.entity_id && (
                        <div className="text-[9px] font-mono text-zinc-600 truncate max-w-[140px]" title={r.entity_id}>
                          {r.entity_id}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
          Only the last 100 events are stored. Failed sends carry the reason in the tooltip.
          Recipients are frozen at send-time — later edits to user emails won&apos;t rewrite historical rows.
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = "default", testId }) {
  const toneMap = {
    default: "text-zinc-100",
    red: "text-[#FF453A]",
    green: "text-[#34C759]",
    amber: "text-[#FF9F0A]",
  };
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={testId}>
      <div className="label-mute">{label}</div>
      <div className={`font-display text-2xl font-black ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}
